import { useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { BFF_ENDPOINTS } from 'src/app/constants';
import networkService from 'src/app/lib/network-service';
import {
  accessTokenAtom,
  conversationsAtom,
  feedbackAtom,
  frameParamsAtom,
} from 'src/app/state/atoms';
import withFeedbackMapper from 'src/app/utils/withFeedbackMapper';
import withSMEMapper from 'src/app/utils/withSMEMapper';
import { IConversations, SMEQueryParams } from 'src/types/serviceResponseTypes';

/**
 * Network hook to bulk fetch conversations for SME Validation
 */
function useSMEConversation() {
  const [conversations, setConversations] = useRecoilState(conversationsAtom);
  const [feedbackData] = useRecoilState(feedbackAtom);
  const [accessToken] = useRecoilState(accessTokenAtom);
  const [frameParams] = useRecoilState(frameParamsAtom);

  const fetchSMEConversation = useCallback(async () => {
    setConversations({
      ...conversations,
      loading: true,
    });

    const smeQueryParams: SMEQueryParams = {
      exercise_id: frameParams.exercise_id,
      book_code: frameParams.book_code ?? '',
      use_feature_flags: false,
    };

    if (frameParams.version != null) {
      smeQueryParams.version = frameParams.version;
    }

    const headers = {
      'X-Authorization': 'Bearer ' + accessToken,
    };

    networkService
      .get(
        (process.env.REACT_APP_BASE_URL ?? '') + BFF_ENDPOINTS.SME_CONVERSATION,
        smeQueryParams,
        headers
      )
      .then((response) => {
        if (response.status) {
          setConversations({
            loading: false,
            data: withSMEMapper(withFeedbackMapper(response.data as IConversations, feedbackData)),
            error: response.error,
          });
        } else {
          setConversations({
            loading: false,
            data: {} as IConversations,
            error: response.error,
          });
        }
      });
  }, [
    setConversations,
    conversations,
    frameParams.exercise_id,
    frameParams.book_code,
    frameParams.version,
    accessToken,
    feedbackData,
  ]);

  return fetchSMEConversation;
}

export default useSMEConversation;
